import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

// Small uppercase section label with a short gold rule.
export function SectionLabel({ children, className = "", testId }) {
  return (
    <span className={cn("fs-label", className)} data-testid={testId}>
      <span className="h-px w-8 bg-fs-gold/70" aria-hidden="true" />
      {children}
    </span>
  );
}

// Primary CTA — dark with a 1px border that warms to gold on hover.
export function GoldButton({
  children,
  to,
  href,
  onClick,
  type = "button",
  variant = "primary",
  className = "",
  testId,
  ...rest
}) {
  const base =
    "group inline-flex items-center justify-center gap-2.5 rounded-full px-7 py-3.5 text-sm font-medium tracking-wide transition-[background-color,border-color,color,box-shadow] duration-300 focus-visible:outline-none";

  const styles = {
    primary:
      "border border-fs-gold/60 bg-fs-gold/[0.06] text-fs-ink hover:border-fs-gold hover:bg-fs-gold/[0.12] hover:shadow-[0_0_28px_-8px_rgba(197,162,74,0.5)]",
    solid:
      "border border-fs-gold bg-fs-gold text-[#0a0a0a] hover:bg-[#d4b45f] hover:shadow-[0_0_32px_-8px_rgba(197,162,74,0.65)]",
    ghost:
      "border border-fs-border bg-transparent text-fs-ink hover:border-fs-gold/50 hover:text-fs-gold",
  };

  const cls = cn(base, styles[variant], className);
  const inner = (
    <>
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
    </>
  );

  if (to) {
    return (
      <Link to={to} className={cls} data-testid={testId} {...rest}>
        {inner}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={cls} data-testid={testId} {...rest}>
        {inner}
      </a>
    );
  }
  return (
    <button type={type} onClick={onClick} className={cls} data-testid={testId} {...rest}>
      {inner}
    </button>
  );
}

// Secondary text button with animated underline.
export function TextButton({ children, to, href, onClick, className = "", testId }) {
  const cls = cn(
    "group inline-flex items-center gap-2 text-sm font-medium tracking-wide text-fs-muted transition-colors duration-300 hover:text-fs-ink",
    className
  );
  const inner = (
    <>
      {children}
      <ArrowRight className="h-4 w-4 text-fs-gold transition-transform duration-300 group-hover:translate-x-1" />
    </>
  );
  if (to) return <Link to={to} className={cls} data-testid={testId}>{inner}</Link>;
  if (href) return <a href={href} className={cls} data-testid={testId}>{inner}</a>;
  return <button onClick={onClick} className={cls} data-testid={testId}>{inner}</button>;
}
